/**
 * Replay engine: reconstruct the model *as it was at a given step* by starting
 * from the nearest cumulative anchor (a full mesh snapshot, or the base scan
 * mesh) and re-applying the operation log on top.
 *
 * Anchor resolution (most→least specific):
 *   1. Snapshot exactly at the target step      → restore it (no replay needed).
 *   2. Latest snapshot before the target step    → restore it, then replay the
 *      deterministic ops for the steps in between.
 *   3. No snapshot ≤ target but a base mesh       → instantiate base, replay all
 *      ops up to the target.
 *
 * A full-screen loader is driven across the whole reconstruction via
 * `editor.setIsLoading`, with the message updated per step so the user sees the
 * model being rebuilt rather than the previous step flashing through.
 */
import useEditorStore from '../../../shared/stores/useEditorStore';
import { GLOBAL_STEP_ORDER } from '../../../shared/constants/flowConfig';
import { hasMeshForStep, restoreMeshForStep } from '../meshSnapshot';
import useOperationLogStore from './operationLogStore';
import { getOperationHandler } from './operationRegistry';
import { hasBaseMesh, instantiateBaseMesh } from './baseMeshSnapshot';

/**
 * Optional bridge to server state. When set, the engine calls this if it has no
 * in-session anchor (snapshot or base) for a step — e.g. after a fresh page load
 * where the user jumps/back-navigates to a step. The resolver should fetch and
 * install the server checkpoint mesh for `stepId` (its cumulative result) and
 * resolve true on success. This is what lets "open Locking after finishing"
 * reconstruct that step instead of rendering blank.
 *
 * @type {((stepId: string, opts: { signal?: AbortSignal }) => Promise<boolean>) | null}
 */
let serverAnchorResolver = null;

/** @param {typeof serverAnchorResolver} resolver */
export function setServerAnchorResolver(resolver) {
  serverAnchorResolver = typeof resolver === 'function' ? resolver : null;
}

/** Human label for the loader. Falls back to the raw id for unknown steps. */
function stepLabel(stepId) {
  return String(stepId || '')
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Find the latest step index ≤ targetIdx that has a cumulative full-mesh snapshot.
 * @returns {number} index into GLOBAL_STEP_ORDER, or -1 if none.
 */
function findAnchorSnapshotIndex(targetIdx) {
  for (let i = targetIdx; i >= 0; i--) {
    if (hasMeshForStep(GLOBAL_STEP_ORDER[i])) return i;
  }
  return -1;
}

/**
 * Apply every logged operation for `stepId` to the current shaded mesh.
 * Returns false if any op lacks a replay handler (caller must fall back).
 * @param {string} stepId
 * @param {AbortSignal} [signal]
 */
async function replayStepOperations(stepId, signal) {
  const ops = useOperationLogStore.getState().getOperationsForStep(stepId);
  if (ops.length === 0) return true;

  for (const op of ops) {
    if (signal?.aborted) return false;
    const handler = getOperationHandler(op.toolId);
    if (!handler) {
      console.warn(`[Replay] no handler for ${op.toolId} (step ${stepId})`);
      return false;
    }

    const editor = useEditorStore.getState();
    const mesh = editor.sceneRefs?.shadedMesh;
    if (!mesh) return false;

    const next = await handler.replay(
      { mesh, scene: editor.sceneRefs?.scene, signal },
      op.params,
    );
    if (next && next !== mesh) {
      useEditorStore.getState().replaceShadedMesh(next);
    } else {
      useEditorStore.getState().incrementGeometryVersion();
    }
  }
  return true;
}

/**
 * Reconstruct and install the model for `targetStepId`.
 *
 * @param {string} targetStepId
 * @param {{ signal?: AbortSignal, showLoader?: boolean }} [opts]
 * @returns {Promise<boolean>} true when the target step was reconstructed.
 */
export async function replayToStep(targetStepId, { signal, showLoader = true } = {}) {
  const targetIdx = GLOBAL_STEP_ORDER.indexOf(targetStepId);
  if (targetIdx < 0) return false;

  const editor = useEditorStore.getState();
  const setLoading = editor.setIsLoading;

  const anchorIdx = findAnchorSnapshotIndex(targetIdx);

  // Case 1: snapshot exactly at the target — fastest, exact, no replay.
  if (anchorIdx === targetIdx) {
    if (showLoader) setLoading(true, `Loading ${stepLabel(targetStepId)}…`);
    const ok = restoreMeshForStep(targetStepId);
    if (showLoader) setLoading(false);
    return ok;
  }

  // Establish the starting mesh.
  let startIdx;
  if (anchorIdx >= 0) {
    if (showLoader) setLoading(true, `Loading ${stepLabel(GLOBAL_STEP_ORDER[anchorIdx])}…`);
    if (!restoreMeshForStep(GLOBAL_STEP_ORDER[anchorIdx])) {
      if (showLoader) setLoading(false);
      return false;
    }
    startIdx = anchorIdx + 1;
  } else if (!hasBaseMesh()) {
    // Case 4: no in-session anchor at all (e.g. fresh page load). Ask the server
    // bridge to install the target step's checkpoint mesh as the result.
    if (!serverAnchorResolver) {
      console.warn('[Replay] no anchor snapshot, base mesh, or server resolver — cannot reconstruct');
      return false;
    }
    if (showLoader) setLoading(true, `Loading ${stepLabel(targetStepId)}…`);
    let ok = false;
    try {
      ok = await serverAnchorResolver(targetStepId, { signal });
    } catch (e) {
      if (e?.name !== 'AbortError') console.warn('[Replay] server anchor resolver failed:', e);
      ok = false;
    }
    // On success the resolver hands the mesh to ThreeScene, which keeps the
    // overlay up until its async load completes and clears it itself (mirrors
    // useBkFinalizationMeshLoad). Only clear here on failure.
    if (showLoader && !ok) setLoading(false);
    return ok;
  } else {
    // Case 3: rebuild from the base scan mesh.
    if (showLoader) setLoading(true, 'Loading base scan…');
    const material = editor.sceneRefs?.material ?? editor.sceneRefs?.shadedMesh?.material;
    const baseMesh = instantiateBaseMesh(material);
    if (!baseMesh || !editor.sceneRefs?.shadedMesh) {
      if (showLoader) setLoading(false);
      return false;
    }
    editor.replaceShadedMesh(baseMesh);
    startIdx = 0;
  }

  // Replay forward, surfacing progress per step.
  for (let i = startIdx; i <= targetIdx; i++) {
    if (signal?.aborted) {
      if (showLoader) setLoading(false);
      return false;
    }
    const stepId = GLOBAL_STEP_ORDER[i];
    if (showLoader) {
      useEditorStore.getState().setLoadingMessage(`Applying ${stepLabel(stepId)}…`);
    }
    const ok = await replayStepOperations(stepId, signal);
    if (!ok) {
      // A step couldn't be replayed and had no snapshot to anchor on. Surface the
      // failure so the caller can decide (e.g. keep current mesh / toast).
      console.warn(`[Replay] unable to reconstruct step ${stepId}`);
      if (showLoader) setLoading(false);
      return false;
    }
  }

  if (showLoader) setLoading(false);
  return true;
}

/** True when `targetStepId` can be reconstructed (snapshot ≤ target, or base + handlers). */
export function canReplayToStep(targetStepId) {
  const targetIdx = GLOBAL_STEP_ORDER.indexOf(targetStepId);
  if (targetIdx < 0) return false;
  if (findAnchorSnapshotIndex(targetIdx) >= 0) return true;
  return hasBaseMesh() || serverAnchorResolver != null;
}
